/**
 * Meta/TikTok Ads Manager view: one card per catalog Product Set (PRODUCT_SET rows),
 * each listing the products (ASSET rows) Leela sent to the publisher's catalog.
 * Publisher-agnostic: Meta and TikTok payload differences are absorbed by
 * catalogProductHelpers. Built from the same pieces as the Google and ADSP views: the
 * shared StatusFilterSelect (default Live), useAdspEntityLevel paging, and
 * AdspPaginationFooter. The status filter narrows products only; product sets are
 * always listed so a set's counts stay visible whatever the filter.
 */
import React, { useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Icon from '@amzn/meridian/icon';
import MeridianLoader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import refreshTokens from '@amzn/meridian-tokens/base/icon/refresh';
import { useTranslation } from 'src/arb-wrapper';
import { Loader } from 'src/components/common/Loader';
import type { Publisher, PublisherEntityDto } from 'src/store/generated/leela-api-generated';
import { AdspPaginationFooter } from './AdspPaginationFooter';
import { CatalogProductSetCard } from './CatalogProductSetCard';
import { StatusFilterSelect } from './StatusFilterSelect';
import type { AdsStatusFilter } from './statusFilterParams';
import { useAdspEntityLevel } from './useAdspEntityLevel';

interface CatalogEntitiesViewProps {
  campaignId: string;
  publisher: Publisher;
  onRefresh?: () => void;
}

const PRODUCT_SET_SORT = ['createdAt:-1'];

export const CatalogEntitiesView: React.FC<CatalogEntitiesViewProps> = ({ campaignId, onRefresh, publisher }) => {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<AdsStatusFilter>('LIVE');

  const { page, query, setPage } = useAdspEntityLevel({
    campaignId,
    publisher,
    publisherEntityType: 'PRODUCT_SET',
    sort: PRODUCT_SET_SORT,
  });

  const productSets = (query.data?.data ?? []) as PublisherEntityDto[];
  const pagination = query.data?.meta.pagination;

  return (
    <Loader isLoading={query.isLoading}>
      <Box data-testid="catalog-entities-view" spacingInset="500" type="outline">
        <Column spacing="400">
          <Row alignmentHorizontal="justify" alignmentVertical="center">
            <Text type="h200">{t('ads_manager_catalog_product_sets_header')}</Text>
            {onRefresh != null && (
              <Row alignmentVertical="center" spacing="300">
                {query.isFetching && <MeridianLoader size="small" type="linear" />}
                <Column alignmentHorizontal="center" spacing="100">
                  <Button
                    data-testid="ads-manager-refresh-button"
                    disabled={query.isFetching}
                    size="small"
                    type="icon"
                    onClick={onRefresh}
                  >
                    <Icon tokens={refreshTokens} />
                  </Button>
                  <Text color="secondary" type="b100">{t('ads_manager_refresh')}</Text>
                </Column>
              </Row>
            )}
          </Row>
          <Divider />

          <Row alignmentVertical="center" spacing="200">
            <Text type="b300">{t('ads_manager_filter_status')}</Text>
            <StatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
          </Row>

          {query.isError && (
            <Alert data-testid="catalog-product-sets-query-error" size="small" type="error">
              {t('ads_manager_query_error')}
            </Alert>
          )}

          {!query.isError && productSets.length === 0 && (
            <Text color="secondary" data-testid="catalog-no-product-sets">
              {t('ads_manager_catalog_no_product_sets')}
            </Text>
          )}

          {productSets.length > 0 && (
            <Column spacing="300">
              {productSets.map((productSet, index) => (
                <CatalogProductSetCard
                  campaignId={campaignId}
                  defaultExpanded={index === 0}
                  key={productSet._id}
                  productSet={productSet}
                  publisher={publisher}
                  statusFilter={statusFilter}
                />
              ))}
            </Column>
          )}

          {pagination != null && (
            <AdspPaginationFooter
              isFetching={query.isFetching}
              page={page}
              pagination={pagination}
              testId="catalog-product-sets"
              onPageChange={setPage}
            />
          )}
        </Column>
      </Box>
    </Loader>
  );
};
